import { bidComparisonResponseSchema, findingSchema, getFindingsResponseSchema, createFindingResponseSchema, updateFindingResponseSchema, getFindingCommentsResponseSchema, createFindingCommentResponseSchema, getFindingsCommentersResponseSchema, getFindingsUniqueBlobsResponseSchema, getFindingsConflictTypeCountResponseSchema, getFindingsStatusCountResponseSchema, getFindingsUniqueLabelsResponseSchema, } from '@consigli/types';
import { createTransformer, createParams, mapToSnakeCase, } from './shared/base-api';
export const findingApiEndpoints = (builder, coreApiBaseUrl) => ({
    getFindings: builder.query({
        query: ({ projectId, packageId, page, pageSize, status, demoView, conflictType, conflictCategory, blobIds, search, language, processorName, commenters, labels, scheduleCheckType, reference, bidDocumentsOnly, }) => {
            const params = createParams({
                page,
                pageSize,
                status,
                demoView,
                conflictType,
                conflictCategory,
                blobIds,
                search,
                language,
                processorName,
                commenters,
                labels,
                scheduleCheckType,
                reference,
                bidDocumentsOnly,
            });
            return {
                url: `${coreApiBaseUrl}/projects/${projectId}/packages/${packageId}/findings/`,
                method: 'GET',
                params: mapToSnakeCase(params),
            };
        },
        providesTags: ['Findings'],
        transformResponse: createTransformer(getFindingsResponseSchema, 'GetFindings'),
    }),
    getBidComparison: builder.query({
        query: ({ projectId, packageId, reference }) => {
            const params = createParams({ reference });
            return {
                url: `${coreApiBaseUrl}/projects/${projectId}/packages/${packageId}/findings/bid-comparison/`,
                method: 'GET',
                params: mapToSnakeCase(params),
            };
        },
        providesTags: ['Findings'],
        transformResponse: createTransformer(bidComparisonResponseSchema, 'GetBidComparison'),
    }),
    getFinding: builder.query({
        query: ({ projectId, packageId, findingId }) => `${coreApiBaseUrl}/projects/${projectId}/packages/${packageId}/findings/${findingId}/`,
        transformResponse: createTransformer(findingSchema, 'getFinding'),
    }),
    getDeletedFindings: builder.query({
        query: ({ projectId, packageId }) => `${coreApiBaseUrl}/projects/${projectId}/packages/${packageId}/findings/soft-deleted/`,
        transformResponse: createTransformer(getFindingsResponseSchema, 'getDeletedFindings'),
    }),
    createFinding: builder.mutation({
        query: ({ projectId, packageId, finding }) => ({
            url: `${coreApiBaseUrl}/projects/${projectId}/packages/${packageId}/findings/`,
            method: 'POST',
            body: {
                ...finding,
            },
        }),
        invalidatesTags: ['Findings'],
        transformResponse: createTransformer(createFindingResponseSchema, 'CreateFindingResponse'),
    }),
    updateFinding: builder.mutation({
        query: ({ projectId, packageId, findingId, data }) => ({
            url: `${coreApiBaseUrl}/projects/${projectId}/packages/${packageId}/findings/${findingId}/`,
            method: 'PATCH',
            body: data,
        }),
        invalidatesTags: ['Findings', 'DashboardData'],
        transformResponse: createTransformer(updateFindingResponseSchema, 'UpdateFinding'),
    }),
    updateBatchFindings: builder.mutation({
        query: ({ projectId, packageId, data }) => ({
            url: `${coreApiBaseUrl}/projects/${projectId}/packages/${packageId}/findings/`,
            method: 'PATCH',
            body: data,
        }),
        invalidatesTags: ['Findings', 'DashboardData'],
        transformResponse: createTransformer(updateFindingResponseSchema, 'UpdateFinding'),
    }),
    deleteAllFindings: builder.mutation({
        query: ({ projectId, packageId, processorName, blobIds, labels, search }) => {
            const params = createParams({ processorName, blobIds, labels, search });
            return {
                url: `${coreApiBaseUrl}/projects/${projectId}/packages/${packageId}/findings/delete-all/`,
                method: 'DELETE',
                params: mapToSnakeCase(params),
            };
        },
        invalidatesTags: ['Findings', 'DashboardData'],
        transformResponse: (raw) => ({
            deletedCount: raw.deleted_count,
        }),
    }),
    getFindingComments: builder.query({
        query: ({ projectId, packageId, findingId }) => `${coreApiBaseUrl}/projects/${projectId}/packages/${packageId}/findings/${findingId}/comments/`,
        providesTags: ['FindingComments'],
        transformResponse: createTransformer(getFindingCommentsResponseSchema, 'GetFindingComments'),
    }),
    createFindingComment: builder.mutation({
        query: ({ projectId, packageId, findingId, text, type }) => ({
            url: `${coreApiBaseUrl}/projects/${projectId}/packages/${packageId}/findings/${findingId}/comments/`,
            method: 'POST',
            body: mapToSnakeCase({
                text,
                type,
            }),
        }),
        invalidatesTags: ['FindingComments', 'Findings', 'FindingCommenters'],
        transformResponse: createTransformer(createFindingCommentResponseSchema, 'CreateExportResponse'),
    }),
    getFindingsCommenters: builder.query({
        query: ({ projectId, packageId, conflictCategory, conflictType, status, labels, demoView, reference, }) => {
            const params = createParams({
                conflictCategory,
                conflictType,
                status,
                labels,
                demoView,
                reference,
            });
            return {
                url: `${coreApiBaseUrl}/projects/${projectId}/packages/${packageId}/findings/commenters/`,
                method: 'GET',
                params: mapToSnakeCase(params),
            };
        },
        transformResponse: createTransformer(getFindingsCommentersResponseSchema, 'GetFindingsCommenters'),
        providesTags: ['FindingCommenters'],
    }),
    getFindingsUniqueBlobs: builder.query({
        query: ({ projectId, packageId, demoView, conflictCategory, conflictType, status, commenters, labels, }) => {
            const params = createParams({
                demoView,
                status,
                conflictCategory,
                conflictType,
                commenters,
                labels,
            });
            return {
                url: `${coreApiBaseUrl}/projects/${projectId}/packages/${packageId}/findings/unique-blobs/`,
                method: 'GET',
                params: mapToSnakeCase(params),
            };
        },
        transformResponse: createTransformer(getFindingsUniqueBlobsResponseSchema, 'GetFindingsUniqueBlobs'),
    }),
    getFindingsConflictTypeCounts: builder.query({
        query: ({ projectId, packageId, blobIds, commenters, labels, demoView, reference, bidDocumentsOnly, }) => {
            const params = createParams({
                blobIds,
                commenters,
                labels,
                demoView,
                reference,
                bidDocumentsOnly,
            });
            return {
                url: `${coreApiBaseUrl}/projects/${projectId}/packages/${packageId}/findings/conflict-type-count/`,
                method: 'GET',
                params: mapToSnakeCase(params),
            };
        },
        providesTags: ['Findings'],
        transformResponse: createTransformer(getFindingsConflictTypeCountResponseSchema, 'GetFindingsConflictTypeCount'),
    }),
    getFindingsStatusCount: builder.query({
        query: ({ projectId, packageId, blobIds, conflictCategory, conflictType, commenters, labels, demoView, reference, bidDocumentsOnly, processorName, search, }) => {
            const params = createParams({
                blobIds,
                conflictCategory,
                conflictType,
                commenters,
                labels,
                demoView,
                reference,
                bidDocumentsOnly,
                processorName,
                search,
            });
            return {
                url: `${coreApiBaseUrl}/projects/${projectId}/packages/${packageId}/findings/status-count/`,
                method: 'GET',
                params: mapToSnakeCase(params),
            };
        },
        providesTags: ['Findings'],
        transformResponse: createTransformer(getFindingsStatusCountResponseSchema, 'GetFindingsStatusCount'),
    }),
    getFindingsUniqueLabels: builder.query({
        query: ({ projectId, packageId, blobIds, status, conflictCategory, conflictType, commenters, demoView, bidDocumentsOnly, }) => {
            const params = createParams({
                blobIds,
                status,
                conflictCategory,
                conflictType,
                commenters,
                demoView,
                bidDocumentsOnly,
            });
            return {
                url: `${coreApiBaseUrl}/projects/${projectId}/packages/${packageId}/findings/unique-labels/`,
                method: 'GET',
                params: mapToSnakeCase(params),
            };
        },
        providesTags: ['Findings'],
        transformResponse: createTransformer(getFindingsUniqueLabelsResponseSchema, 'GetFindingsUniqueLabels'),
    }),
});
